import React, { useState, useMemo } from 'react';
import {
  Table, Button, Drawer, Form, Input, Select, Upload, Space, message,
  Divider, Alert, Modal, Typography, Tag, Tooltip, Popconfirm, Descriptions, Switch
} from 'antd';
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  UploadOutlined, ExclamationCircleFilled, SearchOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
// 当前 mock 角色：'enterprise' — 企业用户（仅见自己企业）；'platform' — 平台管理员（见全部企业）
const MOCK_CURRENT_ROLE = 'enterprise'; // 切换查看效果
const MOCK_CURRENT_ENTERPRISE = { id: 'ent-001', name: '云帆科技' };

const MOCK_ENTERPRISE_MERCHANTS = [
  {
    key: 'test-ent-1', name: 'test-支付宝正式账号', channel: 'alipay',
    enterpriseId: 'ent-001', enterpriseName: '云帆科技',
    createTime: '2025-12-12 18:07:45',
    refApps: [{ name: '鹤梦智能家居', version: 'iOS v2.1' }],
  },
  {
    key: 'test-ent-2', name: 'test-微信收款账号', channel: 'wechat',
    enterpriseId: 'ent-001', enterpriseName: '云帆科技',
    createTime: '2025-12-11 17:52:57',
    refApps: [],
  },
  {
    key: 'test-ent-3', name: 'test-PayPal海外收款', channel: 'paypal',
    enterpriseId: 'ent-001', enterpriseName: '云帆科技',
    createTime: '2025-12-10 15:30:00',
    refApps: [{ name: '智能门锁控制', version: 'Android v1.0' }],
  },
  {
    key: 'test-ent-4', name: 'test-星联支付宝商户', channel: 'alipay',
    enterpriseId: 'ent-002', enterpriseName: '星联智能',
    createTime: '2025-12-09 10:22:00',
    refApps: [],
  },
  {
    key: 'test-ent-5', name: 'test-星联Google Pay', channel: 'googlepay',
    enterpriseId: 'ent-002', enterpriseName: '星联智能',
    createTime: '2025-12-08 09:15:00',
    refApps: [{ name: '健康助手', version: 'iOS v3.0' }, { name: '健康助手', version: 'Android v3.0' }],
  },
  {
    key: 'test-ent-6', name: 'test-深蓝Apple Pay', channel: 'applepay',
    enterpriseId: 'ent-003', enterpriseName: '深蓝科技',
    createTime: '2025-12-07 14:40:00',
    refApps: [],
  },
  {
    key: 'test-ent-7', name: 'test-深蓝Stripe商户', channel: 'stripe',
    enterpriseId: 'ent-003', enterpriseName: '深蓝科技',
    createTime: '2025-12-06 11:20:00',
    refApps: [],
  },
];

const channelConfig = {
  wechat: { label: '微信', fields: ['appId', 'merchantId', 'privateKeyId', 'privateKeyPath', 'publicKeyId', 'publicKeyPath'] },
  alipay: { label: '支付宝', fields: ['appId', 'publicKey', 'privateKey', 'callbackUrl'] },
  paypal: { label: 'PayPal', fields: ['appId', 'customerId', 'customerPrivateKey'] },
  applepay: { label: 'Apple Pay', fields: ['issuerId', 'publicKeyId', 'publicKeyPath'] },
  googlepay: { label: 'Google Pay', fields: ['customerEmail', 'privateKey'] },
  payssion: { label: 'Payssion', fields: ['apiInterfaceKey', 'encryptionKey'] },
  wechat_mini: { label: '微信小程序', fields: ['apiV3Key', 'platformCertificate', 'merchantId', 'merchantCertSerialNo', 'miniAppId', 'merchantApiPrivateKey'] },
  zarinpal: { label: 'ZARINPAL', fields: ['merchantId'] },
  airwallex: { label: 'AIRWALLEX', fields: ['customerId', 'apiKey'] },
  stripe: { label: 'STRIPE', fields: ['publicKey', 'privateKey'] },
  fuyou: { label: '富友支付', fields: ['merchantId', 'privateKey', 'orderPrefix', 'wechatAppId'] },
};

const fieldLabels = {
  appId: 'APP ID', merchantId: '商户ID', privateKeyId: '私钥ID', privateKeyPath: '私钥路径',
  publicKeyId: '公钥ID', publicKeyPath: '公钥路径', publicKey: '公钥', privateKey: '密钥',
  customerId: '客户ID', customerPrivateKey: '客户私钥', issuerId: 'issuerld', customerEmail: '客户邮箱',
  apiInterfaceKey: '应用接口密钥', encryptionKey: '加密密钥', apiV3Key: 'API v3密钥',
  merchantApiPrivateKey: '商户API私钥', platformCertificate: '平台证书', miniAppId: '小程序应用id',
  merchantCertSerialNo: '商户证书序列号', apiKey: 'API密钥', orderPrefix: '订单前缀',
  wechatAppId: '微信APPID', callbackUrl: '回调地址',
};

const EnterpriseMerchant = () => {
  const [form] = Form.useForm();
  // 角色切换（原型演示用，实际使用从 userInfo 获取）
  const [mockRole, setMockRole] = useState(MOCK_CURRENT_ROLE);
  const isPlatform = mockRole === 'platform';

  const [merchants, setMerchants] = useState(MOCK_ENTERPRISE_MERCHANTS);
  const [searchName, setSearchName] = useState('');
  const [searchChannel, setSearchChannel] = useState(undefined);
  const [searchEnterprise, setSearchEnterprise] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);

  // 根据角色和数据范围获取可见的商户号
  const visibleMerchants = useMemo(() => {
    if (isPlatform) return merchants;
    return merchants.filter(m => m.enterpriseId === MOCK_CURRENT_ENTERPRISE.id);
  }, [merchants, isPlatform]);

  // 筛选后的数据
  const filteredMerchants = useMemo(() => {
    let data = visibleMerchants;
    if (searchName) {
      data = data.filter(m => m.name.toLowerCase().includes(searchName.toLowerCase()));
    }
    if (searchChannel) {
      data = data.filter(m => m.channel === searchChannel);
    }
    if (isPlatform && searchEnterprise) {
      data = data.filter(m =>
        m.enterpriseName.toLowerCase().includes(searchEnterprise.toLowerCase()) ||
        m.enterpriseId.toLowerCase().includes(searchEnterprise.toLowerCase())
      );
    }
    return data;
  }, [visibleMerchants, searchName, searchChannel, searchEnterprise, isPlatform]);

  // 同范围下已使用的渠道
  const getUsedChannels = (scopeEnterpriseId) => {
    if (!scopeEnterpriseId) return new Set();
    return new Set(
      merchants.filter(m => m.enterpriseId === scopeEnterpriseId).map(m => m.channel)
    );
  };

  const handleSearch = () => { /* filteredMerchants is computed via useMemo, no-op needed */ };

  const handleReset = () => {
    setSearchName('');
    setSearchChannel(undefined);
    setSearchEnterprise('');
  };

  const handleAdd = () => {
    setEditingMerchant(null);
    setSelectedChannel(null);
    form.resetFields();
    if (!isPlatform) {
      form.setFieldsValue({ enterpriseId: MOCK_CURRENT_ENTERPRISE.id });
    }
    setDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setEditingMerchant(record);
    setSelectedChannel(record.channel);
    form.setFieldsValue(record);
    setDrawerVisible(true);
  };

  const handleDeleteClick = (record) => {
    const refCount = record.refApps?.length || 0;
    Modal.confirm({
      title: '删除商户号',
      icon: <ExclamationCircleFilled />,
      content: refCount > 0
        ? `当前有 ${refCount} 个 App 在使用，确定要删除吗？删除后将影响 App 的支付功能。`
        : '确定要删除该商户号吗？此操作不可恢复。删除后将影响 App 的支付功能。',
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        const updated = merchants.filter(m => m.key !== record.key);
        setMerchants(updated);
        message.success('删除成功');
      },
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const scopeEnterpriseId = editingMerchant
        ? editingMerchant.enterpriseId
        : values.enterpriseId || MOCK_CURRENT_ENTERPRISE.id;
      const enterpriseName = isPlatform
        ? (editingMerchant ? editingMerchant.enterpriseName : '云帆科技' /* mock: selected enterprise name */)
        : MOCK_CURRENT_ENTERPRISE.name;

      if (editingMerchant) {
        const updated = merchants.map(m =>
          m.key === editingMerchant.key
            ? { ...m, ...values, enterpriseId: scopeEnterpriseId, enterpriseName }
            : m
        );
        setMerchants(updated);
        message.success('保存成功');
      } else {
        const newMerchant = {
          key: Date.now().toString(),
          ...values,
          rate: undefined, // 企业商户号无支付费率
          enterpriseId: scopeEnterpriseId,
          enterpriseName,
          createTime: new Date().toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          refApps: [],
        };
        setMerchants([...merchants, newMerchant]);
        message.success('创建成功');
      }
      setDrawerVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDetail = (record) => {
    setSelectedMerchant(record);
    setDetailVisible(true);
  };

  const handleChannelChange = (value) => {
    setSelectedChannel(value);
    form.setFieldsValue({ channel: value });
  };

  // 获取可用渠道（排除同范围已配置的）
  const getAvailableChannels = (scopeEnterpriseId, excludeKey) => {
    const used = getUsedChannels(scopeEnterpriseId);
    return Object.entries(channelConfig).map(([key, config]) => ({
      key, label: config.label,
      disabled: key !== excludeKey && used.has(key),
      reason: key !== excludeKey && used.has(key) ? '该支付渠道已配置' : null,
    }));
  };

  const renderFormFields = () => {
    if (!selectedChannel) return null;
    const config = channelConfig[selectedChannel];
    if (!config) return null;
    return config.fields.map(field => (
      <Form.Item key={field} label={fieldLabels[field]} name={field}>
        {field.includes('Path') || field === 'platformCertificate' ? (
          <Upload maxCount={1}><Button icon={<UploadOutlined />}>上传文件</Button></Upload>
        ) : field.includes('Key') || field === 'privateKey' || field === 'publicKey' ? (
          <Input.TextArea placeholder="请输入" rows={3} />
        ) : (
          <Input placeholder="请输入" />
        )}
      </Form.Item>
    ));
  };

  // 列定义（根据角色不同）
  const columns = useMemo(() => {
    const baseCols = [
      { title: '序号', key: 'index', width: 70, render: (_, __, index) => index + 1 },
      { title: '商户名称', dataIndex: 'name', key: 'name', width: 200 },
      {
        title: '支付渠道', dataIndex: 'channel', key: 'channel', width: 120,
        render: (channel) => <Tag color="blue" style={{ border: 'none' }}>{channelConfig[channel]?.label || channel}</Tag>,
      },
      { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    ];
    if (isPlatform) {
      baseCols.push({
        title: '所属企业', dataIndex: 'enterpriseName', key: 'enterpriseName', width: 200,
        render: (name, record) => (
          <span>
            <Text>{name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>企业ID: {record.enterpriseId}</Text>
          </span>
        ),
      });
    }
    baseCols.push({
      title: '操作', key: 'action', width: 220, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleDetail(record)}>详细信息</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm
            title="删除后不可恢复，确定删除？"
            onConfirm={() => handleDeleteClick(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    });
    return baseCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatform, merchants]);

  const currentScopeEnterpriseId = (editingMerchant?.enterpriseId || MOCK_CURRENT_ENTERPRISE.id);

  return (
    <div style={{ padding: 24 }}>
      {/* 角色切换条（原型演示用，实际环境中由登录态决定） */}
      <div style={{
        marginBottom: 16, padding: '8px 16px', background: '#fff7e6', borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #ffd591'
      }}>
        <Text strong style={{ color: '#ad6800' }}>🔧 角色演示切换：</Text>
        <Switch
          checked={isPlatform}
          onChange={v => setMockRole(v ? 'platform' : 'enterprise')}
          checkedChildren="平台管理员"
          unCheckedChildren="企业用户"
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {isPlatform
            ? '（列表展示全部企业商户号，含所属企业列和企业筛选）'
            : `（仅展示「${MOCK_CURRENT_ENTERPRISE.name}」的商户号）`}
        </Text>
      </div>

      <Title level={4} style={{ marginBottom: 16 }}>企业商户号</Title>

      <Alert
        message={isPlatform
          ? '管理所有企业的商户号配置。可创建、编辑、删除任意企业的商户号，删除有引用的商户号时将引导数据迁移。'
          : '管理本企业的支付商户号配置。每个支付渠道仅允许配置一组商户号。'}
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* 搜索区 */}
      <div style={{ padding: '16px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 8 }}>
        <Space wrap>
          <Input
            placeholder="商户名称"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="支付渠道"
            value={searchChannel}
            onChange={setSearchChannel}
            style={{ width: 150 }}
            allowClear
          >
            {Object.entries(channelConfig).map(([key, config]) => (
              <Select.Option key={key} value={key}>{config.label}</Select.Option>
            ))}
          </Select>
          {isPlatform && (
            <Input
              placeholder="企业名称/ID"
              value={searchEnterprise}
              onChange={e => setSearchEnterprise(e.target.value)}
              style={{ width: 200 }}
              allowClear
              prefix={<SearchOutlined />}
            />
          )}
          <Button type="primary" onClick={handleSearch}>查询</Button>
          <Button onClick={handleReset}>重置</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginLeft: 16 }}>
            创建商户号
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={filteredMerchants}
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        size="small"
        scroll={{ x: isPlatform ? 1150 : 950 }}
        locale={{ emptyText: (
          <div style={{ padding: '40px 0' }}>
            <div style={{ fontSize: 14, color: '#999', marginBottom: 16 }}>
              {isPlatform ? '暂无企业商户号数据' : '暂无商户号，创建您的第一个商户号'}
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>创建商户号</Button>
          </div>
        )}}
      />

      {/* 新建/编辑 Drawer */}
      <Drawer
        title={editingMerchant ? '编辑商户号' : '创建商户号'}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={640}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setDrawerVisible(false)} style={{ marginRight: 8 }}>取消</Button>
            <Button type="primary" onClick={handleSave}>保存</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {/* 平台管理员创建时：显示企业选择器 */}
          {isPlatform && !editingMerchant && (
            <Form.Item label="归属企业" name="enterpriseId" rules={[{ required: true, message: '请选择归属企业' }]}>
              <Select
                placeholder="请选择企业"
                showSearch
                optionFilterProp="children"
                // TODO: 联调时替换为企业搜索接口
                onChange={(val) => { /* 切换企业后重置渠道选择 */ setSelectedChannel(null); form.setFieldsValue({ channel: undefined }); }}
              >
                <Select.Option value="ent-001">云帆科技 (ent-001)</Select.Option>
                <Select.Option value="ent-002">星联智能 (ent-002)</Select.Option>
                <Select.Option value="ent-003">深蓝科技 (ent-003)</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item label="商户名称" name="name" rules={[{ required: true, message: '请输入商户名称' }]}>
            <Input placeholder="请输入商户名称" />
          </Form.Item>

          <Form.Item label="支付渠道" name="channel" rules={[{ required: true, message: '请选择支付渠道' }]}>
            <Select placeholder="请选择" onChange={handleChannelChange}>
              {getAvailableChannels(currentScopeEnterpriseId, editingMerchant?.channel).map(({ key, label, disabled, reason }) => (
                <Select.Option key={key} value={key} disabled={disabled}>
                  <Tooltip title={reason} placement="right">
                    <span>{label}</span>
                  </Tooltip>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 企业商户号不展示支付费率 */}
          <Divider />

          {renderFormFields()}
        </Form>
      </Drawer>

      {/* 详情 Drawer */}
      <Drawer
        title="商户号详情"
        placement="right"
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
        width={600}
      >
        {selectedMerchant && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="商户名称">{selectedMerchant.name}</Descriptions.Item>
                <Descriptions.Item label="支付渠道">
                  <Tag color="blue" style={{ border: 'none' }}>{channelConfig[selectedMerchant.channel]?.label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">{selectedMerchant.createTime}</Descriptions.Item>
                {isPlatform && (
                  <Descriptions.Item label="所属企业">
                    {selectedMerchant.enterpriseName}（企业ID: {selectedMerchant.enterpriseId}）
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>

            <div style={{ fontWeight: 500, marginBottom: 12 }}>渠道配置</div>
            {channelConfig[selectedMerchant.channel]?.fields.map(field => (
              <div key={field} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                <Text type="secondary">{fieldLabels[field]}</Text>
                <Text style={{ maxWidth: '55%', textAlign: 'right', wordBreak: 'break-all' }}>
                  {selectedMerchant[field] || <span style={{ color: '#ccc' }}>-</span>}
                </Text>
              </div>
            ))}

            <Divider />
            <div style={{ fontWeight: 500, marginBottom: 12 }}>引用 App 列表</div>
            {selectedMerchant.refApps?.length > 0 ? (
              <Table
                dataSource={selectedMerchant.refApps.map((app, i) => ({ ...app, key: i }))}
                columns={[
                  { title: 'App 名称', dataIndex: 'name', key: 'name' },
                  { title: '版本', dataIndex: 'version', key: 'version' },
                ]}
                pagination={false}
                size="small"
              />
            ) : (
              <Text type="secondary">暂无 App 引用</Text>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default EnterpriseMerchant;
